import React, { useEffect, useRef, useState } from "react";
import { ConfigProvider, Divider, Input, Radio, Space } from "antd";
import { makeStyles } from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { FIELD_TYPES } from "../constants";
import { isEmpty } from "../../utils/helpers";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";

export default function MultiplierInput({
  cellChecked,
  fieldType = "",
  onMultiplierChange,
  onUniqueChange,
  uniqueNonUnique: uniqueNonUniqueProp,
  multiplier: multiplierProp,
  readOnly = false,
}) {
  const classes = useInputStyles();
  const [multiplier, setMultiplier] = useState(
    isEmpty(multiplierProp) ? "" : multiplierProp
  );
  const [uniqueNonUnique, setUniqueNonUnique] = useState(
    isEmpty(uniqueNonUniqueProp) ? "" : uniqueNonUniqueProp
  );
  const [isInitialMount, setIsInitialMount] = useState(true);
  const prevCellChecked = useRef(cellChecked);

  // Main effect: Handle checkbox state changes and sync with props
  useEffect(() => {
    // Mark as not initial mount after first render
    if (isInitialMount) {
      setIsInitialMount(false);
      prevCellChecked.current = cellChecked;
      return;
    }

    // Detect checkbox state change from true to false: clear values VISUALLY only
    if (prevCellChecked.current === true && cellChecked === false) {
      setMultiplier("");
      setUniqueNonUnique("");
      prevCellChecked.current = cellChecked;
      return;
    }

    // Detect checkbox state change from false to true: restore from props
    if (prevCellChecked.current === false && cellChecked === true) {
      // Restore from props - the mapping still has the original values
      const restoredMultiplier = isEmpty(multiplierProp) ? "1" : multiplierProp;
      const restoredUnique = isEmpty(uniqueNonUniqueProp) ? "" : uniqueNonUniqueProp;
      setMultiplier(restoredMultiplier);
      setUniqueNonUnique(restoredUnique);
      prevCellChecked.current = cellChecked;
      return;
    }

    // Sync with props when checkbox is checked (normal updates from parent)
    if (cellChecked && prevCellChecked.current === true) {
      setMultiplier(isEmpty(multiplierProp) ? "" : multiplierProp);
      setUniqueNonUnique(isEmpty(uniqueNonUniqueProp) ? "" : uniqueNonUniqueProp);
    }

    prevCellChecked.current = cellChecked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellChecked, multiplierProp, uniqueNonUniqueProp, isInitialMount]);

  const handleMultiplierChange = (event) => {
    if (readOnly) return; // Prevent changes when readonly
    
    // Mark as user interaction on first change
    if (isInitialMount) {
      setIsInitialMount(false);
    }
    
    const value = event.target.value.toString();
    
    // Allow free typing - basic validation only
    // Allow digits, one decimal point, and up to 6 characters total
    const basicRegex = /^(\d{0,3}\.?\d{0,2})?$/;
    if (value === "" || basicRegex.test(value)) {
      setMultiplier(value);
    }
  };

  const handleMultiplierBlur = () => {
    if (readOnly) return;
    
    let value = multiplier.toString().trim();
    
    // Handle empty value
    if (value === "") {
      onMultiplierChange("");
      return;
    }
    
    // Remove trailing decimal point (e.g., "0." becomes "0")
    if (value.endsWith('.')) {
      value = value.slice(0, -1);
    }
    
    // If still empty after cleanup, set to empty
    if (value === "") {
      setMultiplier("");
      onMultiplierChange("");
      return;
    }
    
    // Parse and validate the number
    const numValue = parseFloat(value);
    
    if (isNaN(numValue)) {
      // Invalid number, reset to empty or previous valid value
      setMultiplier("");
      onMultiplierChange("");
      return;
    }
    
    // Cap at 100 if exceeds
    if (numValue > 100) {
      setMultiplier("100");
      onMultiplierChange("100");
      return;
    }
    
    // Normalize the value (removes unnecessary zeros, etc.)
    const normalizedValue = numValue.toString();
    setMultiplier(normalizedValue);
    onMultiplierChange(normalizedValue);
  };

  const handleUniqueNonUniqueChange = (event) => {
    if (readOnly) return; // Prevent changes when readonly
    
    // Mark as user interaction on first change
    if (isInitialMount) {
      setIsInitialMount(false);
    }
    
    setUniqueNonUnique(event.target.value);
    onUniqueChange(event.target.value); // Immediate execution for radio buttons
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#08979C",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "3px",
          alignItems: "center",
          marginRight: "3px",
        }}
      >
        <CloseIcon style={{ width: "0.875rem", height: "0.875rem" }} />
        <CustomTooltip title={multiplier}>
          <div style={{ width: "52px" }}>
            <Input
              size="small"
              maxLength={6}
              value={multiplier}
              onChange={handleMultiplierChange}
              onBlur={handleMultiplierBlur}
              disabled={readOnly}
              readOnly={readOnly}
              className={classes.input}
              // placeholder={cellChecked ? "1" : ""}
            />
          </div>
        </CustomTooltip>
        {fieldType === FIELD_TYPES.DATA_MANAGEMENT && (
          <React.Fragment>
            <Divider type="vertical" />
            <div
              style={{
                border: "1px solid var(--surface-300)",
                paddingLeft: "5px",
                borderRadius: "5px",
              }}
            >
              <Radio.Group
                value={uniqueNonUnique}
                onChange={handleUniqueNonUniqueChange}
                size="small"
                disabled={readOnly}
              >
                <Space direction="horizontal">
                  <Radio value="U">U</Radio>
                  <Radio value="N">NU</Radio>
                </Space>
              </Radio.Group>
            </div>
          </React.Fragment>
        )}
      </div>
    </ConfigProvider>
  );
}
const useInputStyles = makeStyles(() => ({
  input: {
    border: "1px solid var(--surface-200)",
    "&:hover": {
      border: "1px solid var(--surface-400)",
      outline: "none",
    },
    "&:focus-visible ": {
      border: "1px solid var(--surface-400)",
      outline: "none",
    },
    "&:focus-within": {
      border: "1px solid var(--surface-400)",
      boxShadow: "none",
      outline: "none",
    },
    "&:disabled": {
      backgroundColor: "var(--blue-gray-100)",
      border: "1px solid var(--surface-200)",
      cursor: "not-allowed",
    },
  },
}));